import React, { useEffect, useRef } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const TERMS_CONTENT = [
  { id: 'eligibility', heading: '1. Eligibility', body: 'Users must be 18+ and legally capable of entering agreements.' },
  { id: 'service-description', heading: '2. Service Description', body: 'ImproveSEO provides AI-powered SEO tools, including:\n• Content generation\n• Optimization tools\n• Cloud-based features' },
  { id: 'license', heading: '3. License', body: 'Users receive a limited, non-transferable license. Prohibited:\n• Reselling\n• Reverse engineering\n• Illegal usage' },
  { id: 'user-content', heading: '4. User Content', body: '• Users retain ownership\n• AI outputs may be inaccurate or non-unique\n• No guarantees for rankings or results' },
  { id: 'ai-disclaimer', heading: '5. AI Disclaimer', body: '• No guarantee of originality\n• User responsible for legal compliance\n• Users indemnify ImproveSEO' },
  { id: 'prohibited-use', heading: '6. Prohibited Use', body: 'Includes illegal, harmful, deceptive, or manipulative actions.' },
  { id: 'third-party-services', heading: '7. Third-Party Services', body: 'Not responsible for third-party integrations.' },
  { id: 'billing', heading: '8. Billing', body: '• Subscription-based\n• Recurring charges unless canceled' },
  { id: 'refunds', heading: '9. Refunds', body: 'Governed by Refund Policy.' },
  { id: 'availability', heading: '10. Availability', body: 'No guarantee of uninterrupted service.' },
  { id: 'account-security', heading: '11. Account Security', body: 'User responsible for account safety.' },
  { id: 'termination', heading: '12. Termination', body: 'Access may be revoked for violations.' },
  { id: 'disclaimer', heading: '13. Disclaimer', body: 'Service provided "as is" without warranties.' },
  { id: 'liability', heading: '14. Liability', body: 'Limited to fees paid or $100.' },
  { id: 'general', heading: '15–23. General', body: '• Force majeure\n• Updates allowed\n• Severability\n• Entire agreement\n• Governing law: New Mexico\n• Disputes handled in New Mexico courts\n• No class actions' },
];

const PRIVACY_CONTENT = [
  { id: 'overview', heading: '1. Overview', body: 'Explains how ImproveSEO collects, uses, and protects personal data in compliance with GDPR and CCPA.' },
  { id: 'data-collected', heading: '2. Data Collected', body: 'Personal Data:\n• Name, email, account details\n• Billing address (used to calculate tax and issue invoices)\n\nTechnical Data:\n• IP, device info, usage logs\n\nContent Data:\n• Prompts, inputs, AI outputs' },
  { id: 'legal-basis', heading: '3. Legal Basis', body: 'Processing based on:\n• Contract performance\n• Legitimate interest\n• Consent\n• Legal obligations' },
  { id: 'usage-purpose', heading: '4. Usage Purpose', body: '• Service delivery\n• Billing and support — including tax calculation, invoicing, and payment processing\n• Security and fraud prevention\n\nWe do not use billing details for marketing.' },
  { id: 'ai-data-usage', heading: '5. AI Data Usage', body: '• Not used to train proprietary models\n• May involve third-party AI providers\n• Uses anonymized data where possible' },
  { id: 'data-sharing', heading: '6. Data Sharing', body: 'Shared only with:\n• Payment processors (Stripe)\n• Cloud/AI providers\n• Legal authorities (if required)' },
  { id: 'international-transfers', heading: '7. International Transfers', body: 'Data may be processed in the US and other countries with safeguards.' },
  { id: 'data-retention', heading: '8. Data Retention', body: 'Stored only as long as necessary, then deleted or anonymized.' },
  { id: 'user-rights', heading: '9. User Rights', body: 'Users may:\n• Access\n• Correct\n• Delete\n• Restrict processing\n• Request portability' },
  { id: 'cookies', heading: '10. Cookies', body: 'Used for functionality, analytics, and performance.' },
  { id: 'security', heading: '11. Security', body: 'Measures in place but no system is 100% secure.' },
  { id: 'children', heading: '12. Children', body: 'Not intended for users under 18.' },
  { id: 'updates', heading: '13. Updates', body: 'Policy may change with notice.' },
  { id: 'contact', heading: '14. Contact', body: 'support@improveseoplugin.com\nLocation: Santa Fe, New Mexico, USA' },
];

/**
 * Anchors other screens deep-link to. Named rather than raw strings so a
 * renumbered policy is a one-line change here instead of a hunt through the
 * app for a section number that silently stopped existing.
 */
export const PRIVACY_SECTIONS = {
  DATA_COLLECTED: 'data-collected',
  USAGE_PURPOSE: 'usage-purpose',
  DATA_SHARING: 'data-sharing',
  USER_RIGHTS: 'user-rights',
};

const LegalModal = ({ open, onClose, type, anchor = null }) => {
  const isTerms = type === 'terms';
  const title = isTerms ? 'Terms of Service' : 'Privacy Policy';
  const lastUpdated = 'January 13, 2026';
  const sections = isTerms ? TERMS_CONTENT : PRIVACY_CONTENT;
  const contentRef = useRef(null);

  // Deep link support: opening with an `anchor` must LAND ON that section, not
  // at the top of the policy with the relevant paragraph somewhere below the
  // fold. The scroll container is DialogContent (scroll="paper"), so this
  // scrolls that element rather than the window.
  useEffect(() => {
    if (!open || !anchor) return undefined;
    // One frame's grace: on the open transition the content is mounted but not
    // yet laid out, and every rect would still read zero.
    const raf = requestAnimationFrame(() => {
      const container = contentRef.current;
      const target = container?.querySelector(`[data-section="${anchor}"]`);
      if (!container || !target) return;
      const delta = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
      container.scrollTop += delta;
    });
    return () => cancelAnimationFrame(raf);
  }, [open, anchor]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: { xs: '95vh', sm: '85vh' },
          m: { xs: 1, sm: 2 },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h5" component="span" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Last Updated: {lastUpdated}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent ref={contentRef} dividers sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
        {sections.map((section) => (
          <Box
            key={section.id}
            id={`${type === 'terms' ? 'terms' : 'privacy'}-${section.id}`}
            data-section={section.id}
            sx={{ mb: 3, scrollMarginTop: '8px' }}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {section.heading}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
            >
              {section.body}
            </Typography>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default LegalModal;
